<script>
import moment from "moment";
import { cloneDeep, isEqual } from "lodash";
import FormModal from "@/components/form-modal.vue";
import GModal from "@/components/g-modal.vue";
import InputV from "@/components/input-v.vue";
import HistoryListModal from "./history-list-modal.vue";
import { postBudgetKey } from "@/api/ativacaoped/budget-keys.service";
import {
  TipoParticipante,
  TipoParticipanteLabel,
  PerfilHistoryColumns,
  BudgetKeyHistoryColumns,
  CentroCustoHistoryColumns
} from "./participants";
import { mapGetters } from "vuex";
import { AnyAdmin } from "@/core/enums/user-types";
import { TiposResponsaveisEnum } from "../responsaveis/responsaveis.types";

export default {
  components: {
    FormModal,
    GModal,
    InputV,
    HistoryListModal
  },
  props: {
    opened: Boolean,
    opts: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: null
    },
    centroCustos: {
      type: Array,
      default: null
    },
    value: {
      type: Object,
      default: () => ({ tipo_participante: TipoParticipante.COLABORADOR })
    }
  },
  model: {
    event: "input",
    prop: "value"
  },
  data() {
    return {
      innerValue: cloneDeep(this.value),
      innerOpened: !!this.opened,
      initial: cloneDeep(this.value),
      perfis: [],
      budgetKeys: [],
      centroCustosLocal: [],
      /** @type {number|null} ID do perfil cujos BKs estão atualmente carregados */
      budgetKeysLoadedForPerfilId: null,
      // Competência inicial de cada vínculo, editável direto no formulário.
      competencias: {
        perfil_id: null,
        budget_key_id: null,
        centro_custo_id: null
      },
      // Snapshot de `competencias` na abertura da modal, para detectar alterações
      // (igual `initial` faz para `innerValue`) e habilitar o botão Salvar.
      initialCompetencias: {
        perfil_id: null,
        budget_key_id: null,
        centro_custo_id: null
      },
      historyModal: {
        opened: false,
        fieldKey: null,
        data: [],
        title: "",
        columns: []
      },
      confirmDesvincular: {
        opened: false,
        fieldKey: null
      },
      // Atalho pra cadastrar uma Budget Key nova quando não há nenhuma
      // disponível pro Perfil selecionado (já vincula ao salvar).
      newBudgetKeyModal: {
        opened: false,
        posicao: ""
      },
      // Corrigir a competência de um registro específico do histórico
      // (aberto pelo lápis na modal de "Ver histórico"), restrito a
      // administrador — usa a rota dedicada `AtivacaoPed.participantes.vinculos.update`.
      editVinculoModal: {
        opened: false,
        fieldKey: null,
        vinculoId: null,
        competencia_inicio: null,
        competencia_fim: null,
        // Hints vindos do próprio histórico (ver `limitesHistorico()` no
        // backend) pra travar o datepicker nos limites que o backend afinal
        // vai validar — só UX, a validação de verdade continua no PUT.
        competenciaInicioMinima: null,
        competenciaFimMaxima: null
      },
      // Confirmação de exclusão de um registro do histórico (lixeira na modal
      // de "Ver histórico"), restrito a administrador — usa a rota dedicada
      // `AtivacaoPed.participantes.vinculos.delete`.
      confirmExcluirVinculo: {
        opened: false,
        fieldKey: null,
        vinculoId: null
      }
    };
  },
  computed: {
    ...mapGetters(["clientId", "user"]),
    // Responsável com nível hierárquico Administrador, ou usuário Galapos
    // (Administrador/Consultor) — mesmo padrão usado em vpl.vue/configs.vue
    // pra outras ações restritas a administrador.
    canManageBudgetKey() {
      const hasAdminRole =
        this.user?.nivel_hierarquico?.tipo_id === TiposResponsaveisEnum.ADMINISTRADOR;

      return hasAdminRole || AnyAdmin.includes(this.user?.tipo_usuario);
    },
    dialogTitle() {
      return this.title !== null
        ? this.title
        : this.isEditing
        ? "Editar participante"
        : "Adicionar participante";
    },
    tiposComBK() {
      return [TipoParticipante.COLABORADOR, TipoParticipante.VAGA_ABERTA];
    },
    budgetKeyVisible() {
      return this.tiposComBK.includes(this.innerValue.tipo_participante);
    },
    vinculoFieldKeys() {
      return ["centro_custo_id", "perfil_id", "budget_key_id"];
    },
    formFieldKeys() {
      // Em modo cadastro os 3 vínculos vão como campo simples (o `forEach`
      // abaixo já resolve); só em edição eles precisam da forma aninhada
      // `{id, competencia_inicio}`, montada à parte em `doSave`.
      return this.fields
        .filter(col => !col.competenciaPara)
        .filter(col => !this.hideField(col.hideInform))
        .map(({ key }) => key)
        .filter(key => !this.isEditing || !this.vinculoFieldKeys.includes(key));
    },
    isEditFormBlocked() {
      return this.fields.some(
        col =>
          !this.hideField(col.hideInform) && col.editable !== false && col.block
      );
    },
    hasChanges() {
      return (
        !isEqual(this.innerValue, this.initial) ||
        !isEqual(this.competencias, this.initialCompetencias)
      );
    },
    perfilSelecionadoLabel() {
      // `senioridade` já vem formatada como "Senioridade | Especialidade"
      // (ver PerfilController::selecao), não precisa concatenar de novo.
      const perfil = this.perfis.find(p => p.id === this.innerValue.perfil_id);
      return perfil ? perfil.senioridade : "";
    },
    // Mesmas regras de "Posição" usadas em budget-key-modal.vue.
    newBudgetKeyPosicaoField() {
      return {
        key: "posicao",
        name: "Posição",
        type: this.$fieldTypes.TEXT,
        rules: [{ rule: "required" }, { rule: "max", params: { size: 255 } }]
      };
    },
    // Config (histórico + nome de exibição) de cada um dos 3 vínculos, usada
    // por "Ver histórico" e pelas mensagens de confirmação do "Desvincular".
    vinculoConfig() {
      return {
        centro_custo_id: {
          fieldName: "Centro de custo",
          columns: CentroCustoHistoryColumns,
          history: this.innerValue.centro_custos ?? []
        },
        perfil_id: {
          fieldName: "Perfil",
          columns: PerfilHistoryColumns,
          history: this.innerValue.perfis ?? []
        },
        budget_key_id: {
          fieldName: "Budget Key",
          columns: BudgetKeyHistoryColumns,
          history: this.innerValue.budget_keys ?? []
        }
      };
    },
    // Config do campo em desvinculação, usada no título e no corpo da modal
    // de confirmação de "Desvincular".
    desvincularFieldConfig() {
      return this.vinculoConfig[this.confirmDesvincular.fieldKey] ?? null;
    },
    // Mapa da coluna declarativa pro array de histórico correspondente em
    // `innerValue`, usado pra atualizar a lista local após corrigir um
    // registro (sem precisar recarregar o participante inteiro).
    historyArrayKey() {
      return {
        perfil_id: "perfis",
        budget_key_id: "budget_keys",
        centro_custo_id: "centro_custos"
      };
    },
    // Alias de `vinculavel_type` (morph map, ver AppServiceProvider) usado
    // pela rota dedicada de correção/exclusão de um registro do histórico.
    vinculoTipo() {
      return {
        perfil_id: "perfil",
        budget_key_id: "budget_key",
        centro_custo_id: "centro_custo"
      };
    },
    editVinculoFields() {
      // O `maxDate` do início e o `minDate` do fim também acompanham o valor
      // que o usuário já escolheu no outro campo (não só os limites fixos do
      // histórico) — senão dá pra escolher um início depois do fim já
      // selecionado, mesmo que dentro dos limites de cada campo isoladamente.
      const {
        competencia_inicio,
        competencia_fim,
        competenciaInicioMinima,
        competenciaFimMaxima
      } = this.editVinculoModal;

      return {
        competencia_inicio: {
          key: "competencia_inicio",
          name: "Competência inicial",
          type: this.$fieldTypes.MONTH,
          minDate: competenciaInicioMinima,
          maxDate: competencia_fim ? `${competencia_fim}-01` : competenciaFimMaxima
        },
        competencia_fim: {
          key: "competencia_fim",
          name: "Competência final",
          type: this.$fieldTypes.MONTH,
          minDate: competencia_inicio ? `${competencia_inicio}-01` : competenciaInicioMinima,
          maxDate: competenciaFimMaxima
        }
      };
    },
    fields() {
      const periodCol = fieldKey => ({
        key: `competencia_inicio_${fieldKey}`,
        name: "Competência Inicial",
        type: this.$fieldTypes.MONTH,
        hideInform: () => !this.isEditing,
        colSize: 3,
        competenciaPara: fieldKey,
        minDate: this.minCompetenciaInicio(fieldKey),
        maxDate: this.maxCompetenciaInicio()
      });

      return [
        {
          key: "tipo_participante",
          name: "Tipo de participante",
          type: this.$fieldTypes.RADIO,
          rules: [{ rule: "required" }],
          rel: { to: "tipoParticipantes", key: "id", name: "value" },
          colSize: 12,
          valueChanged: this.onTipoParticipanteChange
        },
        {
          key: "centro_custo_id",
          name: "Centro de custo",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: {
            to: "centroCustos",
            key: "centro_custo_id",
            name: "centro_custo"
          },
          colSize: this.isEditing ? 6 : this.budgetKeyVisible ? 4 : 6,
          rules: [{ rule: "required" }],
          valueChanged: value => this.setVinculoValue("centro_custo_id", value)
        },
        periodCol("centro_custo_id"),
        {
          key: "perfil_id",
          name: "Perfil",
          type: this.$fieldTypes.AUTOCOMPLETE,
          rel: { to: "perfis", key: "id", name: "senioridade" },
          rules: [{ rule: "required" }],
          colSize: this.isEditing ? 6 : this.budgetKeyVisible ? 4 : 6,
          valueChanged: this.onPerfilChange
        },
        periodCol("perfil_id"),
        {
          key: "budget_key_id",
          name: "Budget key",
          editable: !!this.innerValue.perfil_id,
          placeholder: !this.innerValue.perfil_id
            ? "Selecione um Perfil primeiro"
            : "Selecione um Budget key",
          type: this.$fieldTypes.AUTOCOMPLETE,
          hideInform: !this.tiposComBK.includes(
            this.innerValue.tipo_participante
          ),
          rel: { to: "budgetKeys", key: "id", name: "posicao" },
          colSize: this.isEditing ? 6 : 4,
          valueChanged: value => this.setVinculoValue("budget_key_id", value)
        },
        {
          ...periodCol("budget_key_id"),
          hideInform: () => !this.isEditing || !this.budgetKeyVisible
        },
        {
          key: "matricula",
          name: "Matrícula",
          type: this.$fieldTypes.TEXT,
          hideInform:
            this.innerValue.tipo_participante !== TipoParticipante.COLABORADOR,
          rules: [{ rule: "required" }],
          colSize: 3
        },
        {
          key: "nome",
          name: "Nome do participante",
          type: this.$fieldTypes.TEXT,
          hideInform:
            this.innerValue.tipo_participante != TipoParticipante.COLABORADOR,
          rules: [{ rule: "required" }],
          colSize: 9
        },
        {
          key: "papel_projeto",
          name: "Habilidades",
          type: this.$fieldTypes.TEXT,
          colSize:
            this.innerValue.tipo_participante === TipoParticipante.VAGA_NOVA
              ? 12
              : 6
        },
        {
          key: "formacao",
          name: "Formação técnica",
          type: this.$fieldTypes.TEXT,
          hideInform: ![
            TipoParticipante.COLABORADOR,
            TipoParticipante.VAGA_ABERTA
          ].includes(this.innerValue.tipo_participante),
          rules:
            this.innerValue.tipo_participante === TipoParticipante.VAGA_ABERTA
              ? [{ rule: "required" }]
              : [this.formacaoRule],
          colSize: 6
        },
        {
          key: "email",
          name: "E-mail",
          type: this.$fieldTypes.TEXT,
          hideInform:
            this.innerValue.tipo_participante != TipoParticipante.COLABORADOR,
          rules: [{ rule: "required" }],
          colSize: 6
        },
        {
          key: "linkedin",
          name: "LinkedIn",
          type: this.$fieldTypes.TEXT,
          hideInform:
            this.innerValue.tipo_participante !== TipoParticipante.COLABORADOR,
          rules: [this.linkedinRule],
          colSize: 6
        },
        {
          key: "quantidade",
          name: "Quantidade de vagas",
          type: this.$fieldTypes.NUMBER,
          hideInform:
            this.isEditing ||
            this.innerValue.tipo_participante !== TipoParticipante.VAGA_NOVA,
          rules: [
            { rule: "required" },
            { rule: "min", params: { size: 1 } },
            { rule: "max", params: { size: 50 } }
          ],
          colSize: 3
        }
      ];
    },
    formFields() {
      return this.fields.filter(col => !col.competenciaPara);
    },
    innerOpts() {
      return {
        tipoParticipantes: [
          {
            id: TipoParticipante.COLABORADOR,
            value: TipoParticipanteLabel.COLABORADOR
          },
          {
            id: TipoParticipante.VAGA_ABERTA,
            value: TipoParticipanteLabel.VAGA_ABERTA
          },
          {
            id: TipoParticipante.VAGA_NOVA,
            value: TipoParticipanteLabel.VAGA_NOVA
          }
        ],
        perfis: this.perfis,
        budgetKeys: this.budgetKeys,
        centroCustos: this.centroCustos ?? this.centroCustosLocal,
        ...this.opts
      };
    },
    isEditing() {
      return !!this.innerValue.id;
    }
  },
  watch: {
    clientId(value) {
      if (!value) {
        return;
      }

      // As listas são cacheadas por cliente (ver `opened`) e o registro em
      // edição pertence à empresa anterior: fecha a modal e limpa o cache.
      if (this.innerOpened) {
        this.innerOpened = false;
      }

      this.perfis = [];
      this.budgetKeys = [];
      this.budgetKeysLoadedForPerfilId = null;
      this.centroCustosLocal = [];
      this.getPerfis();

      if (!this.centroCustos) {
        this.getCentroCusto();
      }
    },
    innerOpened(value) {
      if (!isEqual(value, this.opened)) {
        this.$emit("update:opened", value);
      }
    },
    opened(value) {
      if (!isEqual(value, this.innerOpened)) {
        if (value && !this.perfis.length) {
          this.getPerfis();
        }

        if (
          value &&
          this.centroCustos === null &&
          !this.centroCustosLocal.length
        ) {
          this.getCentroCusto();
        }

        if (value) {
          // O watcher de `value` (mais abaixo) ainda não rodou nesse tick, então
          // `innerValue` pode estar com os dados do participante anterior.
          // Sincroniza aqui antes de ler o histórico, para não usar dado velho.
          this.innerValue = cloneDeep(this.value);
          this.initial = cloneDeep(this.value);
          this.competencias = {
            perfil_id: this.getCompetenciaInicioAtual("perfil_id"),
            budget_key_id: this.getCompetenciaInicioAtual("budget_key_id"),
            centro_custo_id: this.getCompetenciaInicioAtual("centro_custo_id")
          };
          this.initialCompetencias = cloneDeep(this.competencias);

          if (
            this.value?.id &&
            this.value?.perfil_id &&
            this.tiposComBK.includes(this.value.tipo_participante)
          ) {
            this.getBudgetKeys(this.value.perfil_id);
          }
        }

        this.innerOpened = value;
      }
    },
    innerValue: {
      deep: true,
      handler(value) {
        if (!isEqual(value, this.value)) {
          this.$emit("input", value);
        }
      }
    },
    value: {
      deep: true,
      handler(value) {
        if (!isEqual(value, this.innerValue)) {
          this.innerValue = value;
        }
      }
    }
  },
  methods: {
    /**
     * Aplica o novo valor de um vínculo (Perfil / Budget Key / Centro de
     * custo), sugerindo a competência inicial de acordo com a troca:
     * mês atual se o valor realmente mudou, ou a competência já vigente se
     * o usuário voltou ao valor original. Usado tanto pela edição direta do
     * select quanto pelo "Desvincular".
     *
     * @param {string} fieldKey
     * @param {number|string|null} newId
     */
    setVinculoValue(fieldKey, newId) {
      this.innerValue = { ...this.innerValue, [fieldKey]: newId };

      const mudou = newId !== this.initial?.[fieldKey];
      this.competencias[fieldKey] = !newId
        ? null
        : mudou
        ? this.competenciaAtual()
        : this.getCompetenciaInicioAtual(fieldKey);

      if (fieldKey !== "perfil_id") return;

      this.budgetKeys = [];
      this.budgetKeysLoadedForPerfilId = null;
      this.innerValue.budget_key_id = null;
      this.competencias.budget_key_id = null;

      if (newId && this.tiposComBK.includes(this.innerValue.tipo_participante)) {
        this.getBudgetKeys(newId);
      }
    },
    competenciaAtual() {
      return moment().format("YYYY-MM");
    },
    onVerHistorico(fieldKey) {
      const config = this.vinculoConfig[fieldKey];
      if (!config) return;

      this.historyModal = {
        opened: true,
        fieldKey,
        data: config.history,
        title: `Histórico de ${config.fieldName}`,
        columns: config.columns
      };
    },
    onDesvincular(fieldKey) {
      this.confirmDesvincular = { opened: true, fieldKey };
    },
    confirmarDesvincular() {
      this.setVinculoValue(this.confirmDesvincular.fieldKey, null);
      this.confirmDesvincular.opened = false;
    },
    /**
     * Atualiza `innerValue[array]` in-place após editar/excluir um registro do
     * histórico, e resincroniza `historyModal.data` (a modal de "Ver histórico"
     * já está aberta nesse ponto, então precisa refletir a mudança sem fechar).
     *
     * @param {string} fieldKey
     * @param {(items: object[]) => object[]} atualizar
     */
    atualizarHistoricoLocal(fieldKey, atualizar) {
      const arrayKey = this.historyArrayKey[fieldKey];
      const atual = this.innerValue[arrayKey] ?? [];

      this.innerValue = { ...this.innerValue, [arrayKey]: atualizar(atual) };

      if (this.historyModal.fieldKey === fieldKey) {
        this.historyModal.data = this.innerValue[arrayKey];
      }
    },
    onEditarVinculo(fieldKey, item) {
      this.editVinculoModal = {
        opened: true,
        fieldKey,
        vinculoId: item.vinculo_id,
        competencia_inicio: item.competencia_inicio,
        competencia_fim: item.competencia_fim,
        competenciaInicioMinima: item.competencia_inicio_minima,
        competenciaFimMaxima: item.competencia_fim_maxima
      };
    },
    async salvarEdicaoVinculo() {
      const { fieldKey, vinculoId, competencia_inicio, competencia_fim } = this.editVinculoModal;
      const tipo = this.vinculoTipo[fieldKey];

      try {
        const { save } = this.apiResource(
          `v1/ativacaoped/${this.clientId}/participantes/${this.innerValue.id}/vinculos/${tipo}`
        );
        await save(
          {
            competencia_inicio: competencia_inicio ?? "",
            competencia_fim: competencia_fim ?? ""
          },
          vinculoId
        );

        this.atualizarHistoricoLocal(fieldKey, items =>
          items.map(item =>
            item.vinculo_id === vinculoId
              ? { ...item, competencia_inicio, competencia_fim }
              : item
          )
        );

        this.editVinculoModal.opened = false;
      } catch (error) {
        // `apiResource(...).save()` já chama `this.notify()` internamente
        // antes de rejeitar (ver `response()` em mixins.vue) — notificar de
        // novo aqui duplicaria o toast de erro.
      }
    },
    onExcluirVinculo(fieldKey, item) {
      this.confirmExcluirVinculo = { opened: true, fieldKey, vinculoId: item.vinculo_id };
    },
    async confirmarExcluirVinculo() {
      const { fieldKey, vinculoId } = this.confirmExcluirVinculo;
      const tipo = this.vinculoTipo[fieldKey];

      try {
        const { delete: excluir } = this.apiResource(
          `v1/ativacaoped/${this.clientId}/participantes/${this.innerValue.id}/vinculos/${tipo}`
        );
        await excluir(vinculoId);

        this.atualizarHistoricoLocal(fieldKey, items =>
          items.filter(item => item.vinculo_id !== vinculoId)
        );

        this.confirmExcluirVinculo.opened = false;
      } catch (error) {
        // `apiResource(...).delete()` já chama `this.notify()` internamente
        // antes de rejeitar (ver `response()` em mixins.vue) — notificar de
        // novo aqui duplicaria o toast de erro.
      }
    },
    onNovaBudgetKey() {
      this.newBudgetKeyModal = { opened: true, posicao: "" };
    },
    async salvarNovaBudgetKey() {
      const posicao = this.newBudgetKeyModal.posicao.trim();
      if (!posicao) return;

      try {
        const result = await postBudgetKey(this.clientId, {
          posicao,
          perfil_id: this.innerValue.perfil_id
        });

        this.newBudgetKeyModal.opened = false;
        await this.getBudgetKeys(this.innerValue.perfil_id);
        this.setVinculoValue("budget_key_id", result.id);
      } catch (error) {
        this.notify(error);
      }
    },
    doSaveEdit() {
      if (!this.validateEditForm()) return;
      this.doSave(this.innerValue, this.closeEditModal);
    },
    validateEditForm() {
      const visibleFields = this.fields
        .filter(col => !this.hideField(col.hideInform))
        .filter(col => col.editable !== false);

      return visibleFields
        .map(col => (col.valid ? !!col.valid() : true))
        .every(Boolean);
    },
    closeEditModal() {
      this.innerOpened = false;
    },
    hideField(hideInform) {
      return hideInform instanceof Function
        ? hideInform(this.innerValue)
        : hideInform;
    },
    valueChanged(valueChanged, value) {
      if (valueChanged) valueChanged(value);
    },
    updateValue(key, val) {
      this.innerValue = { ...this.innerValue, [key]: val };
    },
    updateCompetencia(key, val) {
      this.competencias = { ...this.competencias, [key]: val };
    },
    periodCol(fieldKey) {
      return this.fields.find(
        col => col.key === `competencia_inicio_${fieldKey}`
      );
    },
    historicoDoVinculo(fieldKey) {
      const historyMap = {
        centro_custo_id: "centro_custos",
        perfil_id: "perfis",
        budget_key_id: "budget_keys"
      };
      const history = this.innerValue[historyMap[fieldKey]];
      return Array.isArray(history) ? history : [];
    },
    getCompetenciaInicioAtual(fieldKey) {
      const ativo = [...this.historicoDoVinculo(fieldKey)]
        .filter(item => !item.competencia_fim)
        .sort((a, b) => {
          const inicioA = a.competencia_inicio || "0000-00";
          const inicioB = b.competencia_inicio || "0000-00";
          return inicioB.localeCompare(inicioA);
        })[0];

      return ativo?.competencia_inicio ?? null;
    },
    /**
     * Menor competência inicial permitida, pra não sobrepor/inverter o
     * histórico:
     * - Trocando de valor: a nova competência tem que vir depois do início
     *   do vínculo vigente (é ele quem vai ser fechado nessa troca).
     * - Corrigindo o mesmo valor: só não pode invadir o registro anterior
     *   já encerrado.
     */
    minCompetenciaInicio(fieldKey) {
      const trocou = this.innerValue[fieldKey] !== this.initial?.[fieldKey];

      if (trocou) {
        const vigenteInicio = this.getCompetenciaInicioAtual(fieldKey);
        return vigenteInicio
          ? moment(vigenteInicio).add(1, "month").format("YYYY-MM-DD")
          : null;
      }

      const anterior = [...this.historicoDoVinculo(fieldKey)]
        .filter(item => item.competencia_fim)
        .sort((a, b) => b.competencia_fim.localeCompare(a.competencia_fim))[0];

      return anterior
        ? moment(anterior.competencia_fim).add(1, "day").format("YYYY-MM-DD")
        : null;
    },
    /**
     * Maior competência inicial permitida: o mês atual, nunca uma competência
     * futura (mesma regra validada no backend por `CompetenciaNaoFutura`).
     */
    maxCompetenciaInicio() {
      return moment().endOf("month").format("YYYY-MM-DD");
    },
    doSave(data, closeFn) {
      const newData = {};

      if (this.isEditing) {
        newData.id = data.id;
      }

      this.formFieldKeys.forEach(key => {
        if (key in data) {
          newData[key] = data[key];
        }
      });

      if (!this.isEditing) {
        this.$emit("save", newData, closeFn);
        return;
      }

      // `setVinculoValue`/`onTipoParticipanteChange` já mantêm `innerValue`
      // (e portanto `data`) com o Budget Key correto (null quando o Perfil
      // mudou sem um novo BK compatível, ou quando o tipo deixou de usar BK).
      if ("perfil_id" in data) {
        newData.perfil_id = data.perfil_id;
        newData.perfil = {
          id: data.perfil_id,
          competencia_inicio: this.competencias.perfil_id
        };
      }

      if ("budget_key_id" in data) {
        newData.budget_key_id = data.budget_key_id;
        newData.budget_key = {
          id: data.budget_key_id,
          competencia_inicio: this.competencias.budget_key_id
        };
      }

      if ("centro_custo_id" in data) {
        newData.centro_custo_id = data.centro_custo_id;
        newData.centro_custo = {
          id: data.centro_custo_id,
          competencia_inicio: this.competencias.centro_custo_id
        };
      }

      this.$emit("save", newData, closeFn);
    },
    formacaoRule(value) {
      const safeValue = (value || "").trim();
      const safeRede = (this.innerValue.linkedin || "").trim();
      return !!safeValue || !!safeRede || "Obrigatório se não tiver LinkedIn";
    },
    async getBudgetKeys(perfilId) {
      try {
        const { get } = this.apiResource(
          `v1/ativacaoped/${this.clientId}/budget-keys/selecao`
        );
        const queries = [];

        if (perfilId) {
          queries.push(`filters[perfil_id]=${perfilId}`);
        }

        if (this.value.id) {
          queries.push(`participante_id=${this.value.id}`);
        }

        const response = await get(
          queries.length > 0 ? { query: queries.join("&") } : undefined
        );
        const data = Array.isArray(response.data) ? response.data : [];
        this.budgetKeys = data.sort(
          ({ posicao: a = "" }, { posicao: b = "" }) => a.localeCompare(b)
        );
        this.budgetKeysLoadedForPerfilId = perfilId;

        if (
          data.length === 0 &&
          this.tiposComBK.includes(this.innerValue.tipo_participante)
        ) {
          this.$notify({
            group: "geral",
            duration: 5000,
            type: "info",
            title: "Perfil sem Budget Keys",
            text:
              "O Perfil selecionado não possui posições disponíveis no momento."
          });
        }
      } catch {
        this.budgetKeys = [];
        this.budgetKeysLoadedForPerfilId = null;
      }
    },
    async getPerfis() {
      try {
        const { get } = this.apiResource(
          `v1/ativacaoped/${this.clientId}/perfil/selecao`
        );
        const response = await get();
        this.perfis = response.sort(
          ({ senioridade: a = "" }, { senioridade: b = "" }) =>
            a.localeCompare(b)
        );
      } catch {
        this.perfis = [];
      }
    },
    onTipoParticipanteChange(tipoId) {
      if (!this.tiposComBK.includes(tipoId)) {
        this.setVinculoValue("budget_key_id", null);
        return;
      }

      const perfilId = this.innerValue.perfil_id;

      if (perfilId && perfilId !== this.budgetKeysLoadedForPerfilId) {
        this.budgetKeys = [];
        this.budgetKeysLoadedForPerfilId = null;
        this.getBudgetKeys(perfilId);
      }
    },
    linkedinRule(value) {
      const safeValue = (value || "").trim();
      const safeFormacao = (this.innerValue.formacao || "").trim();
      return (
        !!safeValue || !!safeFormacao || "Obrigatório se não tiver Formação"
      );
    },
    onPerfilChange(perfilId) {
      this.setVinculoValue("perfil_id", perfilId);
    },
    async getCentroCusto() {
      try {
        this.centroCustosLocal = [];
        const { get } = this.apiResource(
          `/v1/ativacaoped/${this.clientId}/centros-de-custo/selecao`
        );
        const response = await get();
        this.centroCustosLocal = response.map(cc => {
          return {
            ...cc,
            centro_custo_id: cc.id,
            centro_custo: cc.nome
          };
        });
      } catch {
        this.centroCustosLocal = [];
      }
    }
  }
};
</script>

<template>
  <div>
    <!-- MODO CADASTRO -->
    <template v-if="!isEditing">
      <FormModal
        :cols="fields"
        contentClass="create-participant-modal"
        :maxWidth="850"
        :opened.sync="innerOpened"
        :opts="innerOpts"
        :title="dialogTitle"
        :value.sync="innerValue"
        :saveDisabled="!hasChanges"
        @save="doSave"
      />
    </template>

    <!-- MODO EDIÇÃO -->
    <template v-else>
      <GModal
        :opened.sync="innerOpened"
        :title="dialogTitle"
        contentClass="create-participant-modal"
        width="850"
        @close="closeEditModal"
      >
        <v-container class="pt-0">
          <v-row class="ml-n4 mr-0">
            <template v-for="col in formFields">
              <template
                v-if="
                  vinculoFieldKeys.includes(col.key) &&
                    (col.key !== 'budget_key_id' || budgetKeyVisible)
                "
              >
                <InputV
                  :key="`${col.key}-input`"
                  class="pl-4"
                  :value="innerValue[col.key]"
                  :label="col"
                  :opts="innerOpts"
                  colSize="7"
                  :valid.sync="col.valid"
                  :block.sync="col.block"
                  :editable="col.editable"
                  :hideInform="hideField(col.hideInform)"
                  @mutate="updateValue(col.key, $event)"
                  @input="updateValue(col.key, $event)"
                  @changed="valueChanged(col.valueChanged, $event)"
                />
                <InputV
                  v-if="periodCol(col.key)"
                  :key="`${col.key}-period-input`"
                  class="pl-4"
                  :value="competencias[col.key]"
                  :label="periodCol(col.key)"
                  :opts="innerOpts"
                  colSize="3"
                  :editable="!!innerValue[col.key]"
                  :hideInform="hideField(periodCol(col.key).hideInform)"
                  @mutate="updateCompetencia(col.key, $event)"
                  @input="updateCompetencia(col.key, $event)"
                />
                <v-col
                  :key="`${col.key}-actions`"
                  cols="2"
                  class="d-flex align-center"
                >
                  <v-btn icon title="Ver histórico" @click="onVerHistorico(col.key)">
                    <v-icon>mdi-timeline-clock-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="
                      col.key === 'budget_key_id' &&
                        canManageBudgetKey &&
                        innerValue.perfil_id &&
                        !budgetKeys.length
                    "
                    icon
                    title="Cadastrar nova Budget Key"
                    @click="onNovaBudgetKey"
                  >
                    <v-icon color="primary">mdi-plus</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="col.key === 'budget_key_id' && canManageBudgetKey"
                    icon
                    title="Desvincular"
                    :disabled="!innerValue[col.key]"
                    @click="onDesvincular(col.key)"
                  >
                    <v-icon color="error">mdi-link-off</v-icon>
                  </v-btn>
                </v-col>
              </template>

              <InputV
                v-else
                :key="col.key"
                class="pl-4"
                :value="innerValue[col.key]"
                :label="col"
                :opts="innerOpts"
                :colSize="col.colSize"
                :valid.sync="col.valid"
                :block.sync="col.block"
                :editable="col.editable"
                :hideInform="hideField(col.hideInform)"
                @mutate="updateValue(col.key, $event)"
                @input="updateValue(col.key, $event)"
                @changed="valueChanged(col.valueChanged, $event)"
              />
            </template>
          </v-row>
        </v-container>

        <template #buttons>
          <v-spacer />
          <v-btn
            class="px-5"
            color="secondary"
            depressed
            @click="closeEditModal()"
          >
            Cancelar
          </v-btn>
          <v-btn
            class="px-5 ml-3"
            color="primary"
            depressed
            :disabled="isEditFormBlocked || !hasChanges"
            @click="doSaveEdit()"
          >
            Salvar
          </v-btn>
        </template>
      </GModal>
    </template>

    <!-- Histórico do vínculo (aberto pelo ícone de caderno) -->
    <HistoryListModal
      v-model="historyModal.opened"
      :data="historyModal.data"
      :title="historyModal.title"
      :columns="historyModal.columns"
      :can-manage="canManageBudgetKey"
      :can-delete="canManageBudgetKey"
      @edit="onEditarVinculo(historyModal.fieldKey, $event)"
      @delete="onExcluirVinculo(historyModal.fieldKey, $event)"
    />

    <!-- Confirmação de "Desvincular" -->
    <GModal
      :opened.sync="confirmDesvincular.opened"
      :title="`Desvincular ${desvincularFieldConfig ? desvincularFieldConfig.fieldName : ''}`"
      width="500"
    >
      <v-alert dense text type="warning" class="mb-0" style="font-size: 13px">
        Tem certeza que deseja desvincular
        <strong v-if="desvincularFieldConfig">{{
          desvincularFieldConfig.fieldName
        }}</strong>? O vínculo vigente será encerrado na data atual.
      </v-alert>

      <template #buttons>
        <v-spacer />
        <v-btn
          class="px-5 ml-3"
          color="secondary"
          text
          @click="confirmDesvincular.opened = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          class="px-5 ml-3"
          color="error"
          depressed
          dark
          @click="confirmarDesvincular"
        >
          Desvincular
        </v-btn>
      </template>
    </GModal>

    <!-- Cadastro rápido de Budget Key (quando não há nenhuma pro Perfil) -->
    <GModal :opened.sync="newBudgetKeyModal.opened" title="Nova Budget Key" width="500">
      <p class="text-caption mb-2">
        Será vinculada ao perfil <strong>{{ perfilSelecionadoLabel }}</strong> e
        já preenchida no participante ao salvar.
      </p>
      <v-row>
        <InputV
          v-model="newBudgetKeyModal.posicao"
          colSize="12"
          :label="newBudgetKeyPosicaoField"
          :valid.sync="newBudgetKeyPosicaoField.valid"
        />
      </v-row>

      <template #buttons>
        <v-spacer />
        <v-btn
          class="px-5 ml-3"
          color="secondary"
          text
          @click="newBudgetKeyModal.opened = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          class="px-5 ml-3"
          color="primary"
          depressed
          dark
          :disabled="!newBudgetKeyPosicaoField.valid || !newBudgetKeyPosicaoField.valid()"
          @click="salvarNovaBudgetKey"
        >
          Salvar
        </v-btn>
      </template>
    </GModal>

    <!-- Corrigir a competência final de um registro do histórico (lápis na
         modal de "Ver histórico") -->
    <GModal :opened.sync="editVinculoModal.opened" title="Corrigir registro do histórico" width="500">
      <v-row>
        <InputV
          v-model="editVinculoModal.competencia_inicio"
          colSize="6"
          :label="editVinculoFields.competencia_inicio"
        />
        <InputV
          v-model="editVinculoModal.competencia_fim"
          colSize="6"
          :label="editVinculoFields.competencia_fim"
        />
      </v-row>

      <template #buttons>
        <v-spacer />
        <v-btn
          class="px-5 ml-3"
          color="secondary"
          text
          @click="editVinculoModal.opened = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          class="px-5 ml-3"
          color="primary"
          depressed
          dark
          @click="salvarEdicaoVinculo"
        >
          Salvar
        </v-btn>
      </template>
    </GModal>

    <!-- Confirmação de exclusão de um registro do histórico (lixeira na
         modal de "Ver histórico") -->
    <GModal
      :opened.sync="confirmExcluirVinculo.opened"
      title="Excluir registro do histórico"
      width="500"
    >
      <v-alert dense text type="warning" class="mb-0" style="font-size: 13px">
        Tem certeza que deseja excluir este registro do histórico? Essa ação
        não pode ser desfeita.
      </v-alert>

      <template #buttons>
        <v-spacer />
        <v-btn
          class="px-5 ml-3"
          color="secondary"
          text
          @click="confirmExcluirVinculo.opened = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          class="px-5 ml-3"
          color="error"
          depressed
          dark
          @click="confirmarExcluirVinculo"
        >
          Excluir
        </v-btn>
      </template>
    </GModal>
  </div>
</template>
